import React, { useState, useEffect, useRef, useMemo } from 'react'
import { X, ChevronLeft, ChevronRight, Eye, Maximize2 } from 'lucide-react'
import { getOptimizedImageUrl } from '../lib/imageOptimization.js'
import { GALLERY_BY_KEY, ALL_REFERENCE_IMAGES } from '../data/galleryData.js'

export default function Gallery({
  galleryKey = 'referencer',
  limit,
  layout = 'carousel', // 'carousel' or 'grid'
  customImages = null
}) {
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const scrollRef = useRef(null)

  // Map known URLs to their curated metadata (alt and title)
  const metaMap = useMemo(() => {
    const map = new Map()
    ALL_REFERENCE_IMAGES.forEach((item) => {
      map.set(item.url, item)
    })
    return map
  }, [])

  // Resolve base images from curated catalog
  const fallbackList = useMemo(() => {
    const list = GALLERY_BY_KEY[galleryKey] || ALL_REFERENCE_IMAGES
    return list.map((item) => ({
      url: item.url,
      alt: item.alt,
      title: item.title || ''
    }))
  }, [galleryKey])

  const [images, setImages] = useState(customImages || fallbackList)

  // Update if galleryKey or customImages changes
  useEffect(() => {
    if (customImages && customImages.length > 0) {
      setImages(customImages)
      return
    }
    const base = GALLERY_BY_KEY[galleryKey] || ALL_REFERENCE_IMAGES
    setImages(base)
  }, [galleryKey, customImages])

  // Try to load any CMS updates in background without blocking initial display
  useEffect(() => {
    if (customImages) return

    let isMounted = true
    const fetchCms = async () => {
      try {
        const res = await fetch('/_api/public/content/galleri')
        if (!res.ok) return
        const data = await res.json()
        const items = Array.isArray(data) ? data : (data.items || [])

        const match = items.find((item) => item.key === galleryKey)
        if (match && Array.isArray(match.images) && match.images.length > 0 && isMounted) {
          const formatted = match.images.map((imgUrl, i) => {
            const known = metaMap.get(imgUrl)
            if (known) {
              return { url: imgUrl, alt: known.alt, title: known.title }
            }
            return {
              url: imgUrl,
              alt: `Tømrerarbejde udført af Tømrermester Daniel Vandet - reference ${i + 1}`,
              title: `Referenceprojekt ${i + 1}`
            }
          })
          setImages(formatted)
        }
      } catch {
        // Fallback already in state
      }
    }

    fetchCms()
    return () => {
      isMounted = false
    }
  }, [galleryKey, customImages, metaMap])

  const displayedImages = limit ? images.slice(0, limit) : images

  // Carousel navigation
  const scrollToIndex = (index) => {
    if (!scrollRef.current) return
    const container = scrollRef.current
    const cards = container.querySelectorAll('[data-gallery-card]')
    if (cards[index]) {
      const containerRect = container.getBoundingClientRect()
      const cardRect = cards[index].getBoundingClientRect()
      const offset = cardRect.left - containerRect.left
      container.scrollTo({
        left: container.scrollLeft + offset,
        behavior: 'smooth'
      })
      setCurrentIndex(index)
    }
  }

  const handlePrev = () => {
    if (displayedImages.length <= 1) return
    const nextIdx = currentIndex === 0 ? displayedImages.length - 1 : currentIndex - 1
    scrollToIndex(nextIdx)
  }

  const handleNext = () => {
    if (displayedImages.length <= 1) return
    const nextIdx = currentIndex >= displayedImages.length - 1 ? 0 : currentIndex + 1
    scrollToIndex(nextIdx)
  }

  // Update currentIndex on scroll
  const handleScroll = () => {
    if (!scrollRef.current) return
    const container = scrollRef.current
    const cards = container.querySelectorAll('[data-gallery-card]')
    if (!cards.length) return

    const containerLeft = container.getBoundingClientRect().left
    let closestIndex = 0
    let minDiff = Infinity

    cards.forEach((card, idx) => {
      const cardLeft = card.getBoundingClientRect().left
      const diff = Math.abs(cardLeft - containerLeft)
      if (diff < minDiff) {
        minDiff = diff
        closestIndex = idx
      }
    })

    setCurrentIndex(closestIndex)
  }

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxIndex(null)
    document.body.style.overflow = 'unset'
  }

  const navigateLightbox = (direction, e) => {
    if (e) e.stopPropagation()
    if (displayedImages.length === 0) return

    if (direction === 'next') {
      setLightboxIndex((prev) => (prev + 1) % displayedImages.length)
    } else {
      setLightboxIndex((prev) => (prev - 1 + displayedImages.length) % displayedImages.length)
    }
  }

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') navigateLightbox('next')
      if (e.key === 'ArrowLeft') navigateLightbox('prev')
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxIndex, displayedImages])

  if (!displayedImages || displayedImages.length === 0) {
    return null
  }

  return (
    <div className="relative">
      {/* Karruselvisning */}
      {layout === 'carousel' ? (
        <div className="space-y-4">
          {/* Top navigation controls info & buttons */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs sm:text-sm text-stone-500 font-medium">
              Viser {currentIndex + 1} ud af {displayedImages.length} billeder
              <span className="hidden sm:inline text-stone-400 ml-2">
                • Klik på et billede for at forstørre
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                disabled={displayedImages.length <= 1}
                aria-label="Forrige billede i galleri"
                className={`p-2 sm:p-2.5 rounded-full border transition-all duration-200 cursor-pointer ${
                  displayedImages.length <= 1
                    ? 'border-stone-200 text-stone-300 cursor-not-allowed bg-stone-50'
                    : 'border-stone-300 text-stone-700 bg-white hover:bg-[#c08f66] hover:text-white hover:border-[#c08f66] shadow-sm'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <button
                onClick={handleNext}
                disabled={displayedImages.length <= 1}
                aria-label="Næste billede i galleri"
                className={`p-2 sm:p-2.5 rounded-full border transition-all duration-200 cursor-pointer ${
                  displayedImages.length <= 1
                    ? 'border-stone-200 text-stone-300 cursor-not-allowed bg-stone-50'
                    : 'border-stone-300 text-stone-700 bg-white hover:bg-[#c08f66] hover:text-white hover:border-[#c08f66] shadow-sm'
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Horisontal rulle-karrusel med snap */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            tabIndex={0}
            role="region"
            aria-label="Billedkarrusel over referenceprojekter"
            className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-stone-300 scrollbar-track-transparent focus:outline-none"
            style={{ scrollbarWidth: 'thin' }}
          >
            {displayedImages.map((img, index) => (
              <div
                key={img.url + index}
                data-gallery-card
                onClick={() => openLightbox(index)}
                className="group relative flex-none w-[82vw] sm:w-[46vw] lg:w-[31.5%] snap-start overflow-hidden rounded-sm border border-stone-200 bg-white shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
              >
                {/* Billedecontainer med fast aspect ratio */}
                <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
                  <img
                    src={getOptimizedImageUrl(img.url, { width: 700, quality: 80 })}
                    alt={img.alt || `Tømrerarbejde reference ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  />

                  {/* Hover overlay med ikon */}
                  <div className="absolute inset-0 bg-stone-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-4 text-center text-white">
                    <div className="w-11 h-11 rounded-full bg-[#c08f66] flex items-center justify-center mb-2 shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      <Maximize2 className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-stone-200">
                      Klik for stor visning
                    </span>
                  </div>
                </div>

                {/* Billedetekst under billedet */}
                <div className="p-3.5 bg-white border-t border-stone-100">
                  <h4 className="text-sm font-bold text-[#1e1e1e] line-clamp-1 group-hover:text-[#c08f66] transition-colors">
                    {img.title || `Projekt reference ${index + 1}`}
                  </h4>
                  <p className="text-xs text-[#475569] line-clamp-2 mt-0.5 leading-relaxed">
                    {img.alt}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Dots indikatorer for karrusellen */}
          {displayedImages.length > 1 && (
            <div className="flex items-center justify-center gap-1.5 pt-2">
              {displayedImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => scrollToIndex(i)}
                  aria-label={`Gå til billede ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-200 cursor-pointer ${
                    currentIndex === i ? 'w-6 bg-[#c08f66]' : 'w-2 bg-stone-300 hover:bg-stone-400'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Gittervisning (grid) */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedImages.map((img, index) => (
            <div
              key={img.url + index}
              onClick={() => openLightbox(index)}
              className="group relative overflow-hidden rounded-sm border border-stone-200 bg-white shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
                <img
                  src={getOptimizedImageUrl(img.url, { width: 700, quality: 80 })}
                  alt={img.alt || `Tømrerarbejde reference ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-stone-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center text-white">
                  <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider bg-[#c08f66] px-4 py-2 rounded-sm shadow-md">
                    <Eye className="w-4 h-4" /> Se billede
                  </span>
                </div>
              </div>

              <div className="p-3.5 bg-white border-t border-stone-100">
                <h4 className="text-sm font-bold text-[#1e1e1e] line-clamp-1 group-hover:text-[#c08f66] transition-colors">
                  {img.title || `Projekt reference ${index + 1}`}
                </h4>
                <p className="text-xs text-[#475569] line-clamp-2 mt-0.5 leading-relaxed">
                  {img.alt}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox / Fuldskærmsvisning */}
      {lightboxIndex !== null && displayedImages[lightboxIndex] && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-stone-950/95 p-3 sm:p-6 animate-in fade-in duration-200"
        >
          {/* Luk-knap */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-[110] rounded-full bg-stone-900/90 hover:bg-[#c08f66] text-white p-3 transition-colors cursor-pointer shadow-lg"
            aria-label="Luk stor visning"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Forrige / Næste knapper */}
          {displayedImages.length > 1 && (
            <>
              <button
                onClick={(e) => navigateLightbox('prev', e)}
                className="absolute left-3 sm:left-6 z-[110] rounded-full bg-stone-900/90 hover:bg-[#c08f66] text-white p-3 transition-colors cursor-pointer shadow-lg"
                aria-label="Forrige billede"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>

              <button
                onClick={(e) => navigateLightbox('next', e)}
                className="absolute right-3 sm:right-6 z-[110] rounded-full bg-stone-900/90 hover:bg-[#c08f66] text-white p-3 transition-colors cursor-pointer shadow-lg"
                aria-label="Næste billede"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Billede container */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] max-w-[94vw] flex flex-col items-center justify-center"
          >
            <img
              src={getOptimizedImageUrl(displayedImages[lightboxIndex].url, {
                width: 1600,
                quality: 85
              })}
              onError={(e) => {
                e.currentTarget.onerror = null
                e.currentTarget.src = displayedImages[lightboxIndex].url
              }}
              alt={displayedImages[lightboxIndex].alt}
              className="max-h-[75vh] w-auto max-w-full object-contain rounded-sm shadow-2xl bg-stone-900"
              decoding="async"
            />

            {/* Billedtekst og tæller i bunden */}
            <div className="mt-4 max-w-2xl text-center bg-stone-900/90 text-white px-5 py-3 rounded-sm border border-white/10 backdrop-blur-sm shadow-lg">
              <div className="text-xs uppercase tracking-wider text-[#c08f66] font-semibold mb-1">
                Billede {lightboxIndex + 1} af {displayedImages.length}
                {displayedImages[lightboxIndex].title && ` • ${displayedImages[lightboxIndex].title}`}
              </div>
              <p className="text-xs sm:text-sm text-stone-200 leading-relaxed">
                {displayedImages[lightboxIndex].alt}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
